import {ChangeDetectionStrategy, Component, DestroyRef, effect, Injectable, input, output} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {EnkelGruppeModell, GruppeId, GruppeMedlemListeModell, GruppeRessursClient, MedlemId, MedlemsRolleModell, RedigerGruppeInvitasjonSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatRadioModule} from "@angular/material/radio";
import {MatIconModule} from "@angular/material/icon";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatOptionModule} from "@angular/material/core";
import {MatSelectModule} from "@angular/material/select";
import {TranslateModule} from "@ngx-translate/core";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatTooltip} from "@angular/material/tooltip";
import {MedlemInvitasjonSkjemaKomponent, MedlemInvitasjonSkjemaKomponentAvhengigheter, MedlemInvitasjonSkjemaKomponentAvhengigheter_DI_TOKEN} from "../medlem-invitasjon-skjema/medlem-invitasjon-skjema";
import {InlineSkjemaKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema";
import {InlineSkjemaTittelKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-tittel/inline-skjema-tittel";
import {InlineSkjemaHandlingerKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-handlinger/inline-skjema-handlinger";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {MatButton} from "@angular/material/button";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {Observable} from "rxjs";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {eksistererMedlemMedEpost, eksistererMedlemMedNavn, medlemmerIkkeAnonymisert, navnFraMedlemModell, ValiderEpostRespons} from "felles-ng2/modell/medlemModellHjelpere";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {lesPlainDateSerialisert, lesPlainDateSerialisertOpt, skrivPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug = initDebugger('MedlemInvitasjonRedigerKomponent');


@Component({
    selector: 'medlem-invitasjon-rediger',
    templateUrl: './medlem-invitasjon-rediger.html',
    styleUrl: './medlem-invitasjon-rediger.scss',
    providers: [{provide: MedlemInvitasjonSkjemaKomponentAvhengigheter_DI_TOKEN, useExisting: MedlemInvitasjonRedigerKomponent}],
    imports: [
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatRadioModule,
        MatIconModule,
        MatDatepickerModule,
        ReactiveFormsModule,
        MatOptionModule,
        MatSelectModule,
        MatSlideToggle,
        MatTooltip,
        MatButton,
        TranslateModule,
        MedlemInvitasjonSkjemaKomponent,
        InlineSkjemaKomponent,
        InlineSkjemaTittelKomponent,
        InlineSkjemaHandlingerKomponent,
        NavigasjonforhindretFlashDirektiv,
        VisSpinnerDirektiv
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemInvitasjonRedigerKomponent implements MedlemInvitasjonSkjemaKomponentAvhengigheter {

    vedLagret = output<void>();
    vedAvbryt = output<void>();

    gruppe = input.required<EnkelGruppeModell>();
    invitasjon = input.required<GruppeMedlemListeModell>()

    skjema = new FormGroup({
        invitasjon: MedlemInvitasjonSkjemaKomponent.opprettSkjema(),
        sendInvitasjon: new FormControl(false, {nonNullable: true})
    });
    lagrer = asynkArbeidPågårSignal();

    private alleMedlemmer: GruppeMedlemListeModell[] = [];

    constructor(private avhengigheter: MedlemInvitasjonRedigerKomponentAvhengigheter,
                private meldingTilBruker: MeldingTilBruker,
                private oversettelser: Oversettelser,
                private destroyRef: DestroyRef) {

        //oppdater skjema når invitasjon endres
        effect(() => this.oppdaterSkjema(this.invitasjon()));

        effect(() => {
            this.avhengigheter.medlemmer(this.gruppe().id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(medlemmer => {
                  this.alleMedlemmer = medlemmer
              });
        });
    }

    async lagre() {
        await this.lagrer.utfør(async () => {
            const spesifikasjon = this.somSpesifikasjon();
            await this.avhengigheter.oppdater(this.invitasjon().id, spesifikasjon);
            if (spesifikasjon.sendInvitasjon)
                this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Invitasjon sendt til {epost}'), {epost: spesifikasjon.epost}));
            else
                this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Invitasjon oppdatert')));
            this.vedLagret.emit();
        });
    }

    avbryt() {
        this.vedAvbryt.emit()
    }

    //MedlemInvitasjonSkjemaKomponentAvhengigheter implementasjon

    medlemsroller(): Observable<MedlemsRolleModell[]> {
        return this.avhengigheter.medlemsroller(this.gruppe().id);
    }

    verifiserAtEpostIkkeAlleredeErRegistrert(epost: string): ValiderEpostRespons {
        const epostEndret = epost !== this.invitasjon().epost;
        this.skjema.controls.sendInvitasjon.setValue(epostEndret);
        epostEndret ? this.skjema.controls.sendInvitasjon.enable() : this.skjema.controls.sendInvitasjon.disable();

        if (!epostEndret) return {
            eksistererMedlem: false,
            eksistererInvitasjon: false,
            eksistererInaktivt: false
        }
        return eksistererMedlemMedEpost(this.alleMedlemmer, epost);
    }

    /** Sann dersom det ikke finnes et medlem med samme navn (casing ignorert), ignorer permanent deaktiverte medlemmer */
    verifiserAtNavnIkkeAlleredeErRegistrert(navn: string): boolean {
        if (navn === this.invitasjon().navn) return false;
        return eksistererMedlemMedNavn(medlemmerIkkeAnonymisert(this.alleMedlemmer), navn);
        return eksistererMedlemMedNavn(this.alleMedlemmer, navn);
    }

    //private hjelpemetoder

    private oppdaterSkjema(invitasjon:GruppeMedlemListeModell) {
        const navn = navnFraMedlemModell(invitasjon);
        // Rollen tildelt invitasjonen, merk at dette ikke nødvendigvis er en aktiv rolle, tar derfor den første rollen fra samlingen aktive+inaktive medlemsroller
        const rolle = første([...invitasjon.aktiveMedlemsroller, ...invitasjon.inaktiveMedlemsroller]);
        this.skjema.patchValue({
            invitasjon: {
                navn: navn,
                epost: invitasjon.epost,
                mobilnummer: invitasjon.mobilnummer,
                //MedlemsRollePeriode2Modell inneholder ikke MedlemsRolleModell, konstruere MedlemsRolleModell representasjon her
                rolle: erIkkeNil(rolle) ? {id: rolle.medlemsrolleId, tittel: rolle.tittel, indeks: rolle.indeks, kanIkkeSlettes: false} : null,
                rolleStartdato: lesPlainDateSerialisertOpt(rolle?.periode?.start)
            }
        });
        //tilgangsroller må settes via eget kall
        MedlemInvitasjonSkjemaKomponent.setTilgangsroller(this.skjema.controls.invitasjon, invitasjon.tilgangsroller);
    }

    private somSpesifikasjon(): RedigerGruppeInvitasjonSpesifikasjon {
        const verdier = this.skjema.getRawValue();
        return {
            navn: verdier.invitasjon!.navn,
            epost: verdier.invitasjon!.epost,
            mobilnummer: verdier.invitasjon!.mobilnummer,
            rolleId: verdier.invitasjon!.rolle!.id,
            rolleStartdato: skrivPlainDateSerialisert(verdier.invitasjon!.rolleStartdato!),
            tilgangsroller: MedlemInvitasjonSkjemaKomponent.lesTilgangsroller(this.skjema.controls.invitasjon),

            sendInvitasjon: verdier.sendInvitasjon
        }
    }
}


@Injectable()
export class MedlemInvitasjonRedigerKomponentAvhengigheter {

    constructor(private gruppeRessurs: GruppeRessursClient, private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste) {
    }

    /** Returnerer tilgjengelige medlemsroller */
    medlemsroller(gruppeId: GruppeId): Observable<MedlemsRolleModell[]> {
        return this.gruppeRessurs.finnTilgjengeligeRoller(gruppeId).somObservable();
    }

    /** Returnerer alle medlemmer tilknyttet gruppen */
    medlemmer(gruppeId: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemDatatjeneste.finnTilknyttet(gruppeId);
    }

    async oppdater(id: MedlemId, spesifikasjon: RedigerGruppeInvitasjonSpesifikasjon): Promise<void> {
        await this.gruppemedlemDatatjeneste.oppdaterInvitasjon(id, spesifikasjon);
    }
}
